import React from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Flame, Crown } from "lucide-react";
import CoinIcon from "@/components/landing/CoinIcon";

const BUNDLES = [
    { coins: 10, price: "$1.00", tag: null, accent: false },
    { coins: 50, price: "$3.00", tag: "tagSave40", accent: false },
    { coins: 100, price: "$5.00", tag: "tagSave50", accent: false },
    { coins: 200, price: "$10.00", tag: "tagPopular", accent: true, badgeIcon: Flame },
    { coins: 500, price: "$20.00", tag: "tagSave60", accent: false },
    { coins: 1000, price: "$30.00", tag: "tagSave70", accent: false },
    { coins: 5000, price: "$100.00", tag: "tagBestValue", accent: true, badgeIcon: Crown },
];

export default function CoinBundles() {
    const reduce = useReducedMotion();
    const { t } = useTranslation();
    return (
        <section id="coins" className="py-16 sm:py-20 lg:py-24 bg-[#FBF7EE]" data-testid="coins-section">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="flex flex-col items-start gap-4 max-w-2xl">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#F4EBDD] border border-[#E9D9C1] px-3 py-1 text-xs font-semibold text-[#0B4A34] uppercase tracking-wider"><CoinIcon className="h-4 w-4" />{t("coins.eyebrow")}</span>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#062A1E]">{t("coins.title")}</h2>
                    <p className="text-[#1B2621]/75 text-base sm:text-lg">{t("coins.subtitle_lead")} <span className="font-semibold text-[#0B4A34]">{t("coins.subtitle_strong")}</span> {t("coins.subtitle_tail")}</p>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                    {BUNDLES.map((b, idx) => {
                        const BadgeIcon = b.badgeIcon;
                        return (
                            <motion.div key={b.coins} initial={reduce ? false : { opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.45, delay: idx * 0.05 }} className={`relative rounded-2xl p-5 sm:p-6 border ${b.accent ? "border-[#C8A24A] bg-gradient-to-b from-[#FFFDF7] to-[#FFF6DD] shadow-[0_18px_50px_rgba(200,162,74,0.25)]" : "border-[#E9D9C1] bg-[#FFFDF7] shadow-[0_10px_30px_rgba(6,42,30,0.06)] hover:shadow-[0_18px_50px_rgba(6,42,30,0.12)]"} hover:-translate-y-0.5 transition-all duration-200`} data-testid={`coin-bundle-${b.coins}`}>
                                {b.tag && (
                                    <div className={`absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${b.accent ? "bg-[#0B4A34] text-[#E0C06A] border border-[#0B4A34]" : "bg-[#F4EBDD] text-[#8B6F2A] border border-[#E9D9C1]"}`}>
                                        {BadgeIcon && <BadgeIcon className="h-3 w-3" />}
                                        {t(`coins.${b.tag}`)}
                                    </div>
                                )}
                                <div className="flex items-center gap-3">
                                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FBF7EE] border border-[#E9D9C1]"><CoinIcon className="h-7 w-7" /></span>
                                    <div className="min-w-0">
                                        <div className="font-display text-2xl sm:text-3xl text-[#062A1E] leading-none">{b.coins.toLocaleString()}</div>
                                        <div className="text-xs font-semibold tracking-wider text-[#5B6B63] uppercase mt-0.5">{t("coins.coinsLabel")}</div>
                                    </div>
                                </div>
                                <div className="mt-5">
                                    <div className="text-2xl sm:text-3xl font-bold text-[#062A1E] leading-none">{b.price}</div>
                                </div>
                                <Button asChild className={`mt-5 w-full h-10 rounded-xl font-semibold ${b.accent ? "bg-[#C8A24A] hover:bg-[#E0C06A] text-[#0B0F0D]" : "bg-[#0B4A34] hover:bg-[#0F6244] text-[#FBF7EE]"}`} data-testid={`coin-bundle-cta-${b.coins}`}>
                                    <a href="#waitlist">{t("coins.notifyCta")}</a>
                                </Button>
                            </motion.div>
                        );
                    })}
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between rounded-2xl border border-[#C8A24A] bg-gradient-to-r from-[#FFF6DD] to-[#FBF7EE] p-5 shadow-[0_18px_50px_rgba(200,162,74,0.18)]">
                    <div className="flex items-start gap-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#0B4A34] text-[#E0C06A] flex-shrink-0"><CoinIcon className="h-6 w-6" /></span>
                        <div className="text-sm text-[#1B2621]/90">
                            <div className="font-bold text-[#062A1E] text-base">{t("coins.bonusTitle")}</div>
                            <div className="mt-0.5">{t("coins.bonusBody")}</div>
                        </div>
                    </div>
                    <Button asChild className="bg-[#0B4A34] hover:bg-[#0F6244] text-[#FBF7EE] h-10 px-5 rounded-xl font-semibold w-full sm:w-auto" data-testid="coins-section-waitlist-cta">
                        <a href="#waitlist">{t("coins.bonusCta")}</a>
                    </Button>
                </div>
                <p className="mt-4 text-xs text-[#1B2621]/60">{t("coins.footnote")}</p>
            </div>
        </section>
    );
}
